import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faTrash,
  faGripVertical,
  faChevronDown,
  faChevronUp,
  faLayerGroup,
} from "@fortawesome/free-solid-svg-icons";
import { PARAMETER_TYPES } from "../services/productParameters";
import { v4 as uuidv4 } from "uuid";

const ParameterTemplateEditor = ({
  parameters = [],
  onChange,
  disabled = false,
}) => {
  const [expandedParams, setExpandedParams] = useState({});

  // Adicionar novo campo
  const addParameter = () => {
    const newParam = {
      id: uuidv4(),
      name: "",
      type: "text",
      required: false,
      order: parameters.length + 1,
      options: [],
      min: undefined,
      max: undefined,
    };
    onChange([...parameters, newParam]);
    setExpandedParams({ ...expandedParams, [newParam.id]: true });
  };

  // Adicionar nova secção (grupo de campos com accordion)
  const addGroup = () => {
    const newGroup = {
      id: uuidv4(),
      type: "group",
      name: "",
      order: parameters.length + 1,
      children: [],
    };
    onChange([...parameters, newGroup]);
    setExpandedParams({ ...expandedParams, [newGroup.id]: true });
  };

  // Remover parâmetro ou secção
  const removeParameter = (paramId) => {
    if (window.confirm("Tem certeza que deseja remover este item?")) {
      const newParams = parameters.filter((p) => p.id !== paramId);
      const reordered = newParams.map((p, index) => ({
        ...p,
        order: index + 1,
      }));
      onChange(reordered);
    }
  };

  // Atualizar campo/secção
  const updateParameter = (paramId, field, value) => {
    const newParams = parameters.map((p) =>
      p.id === paramId ? { ...p, [field]: value } : p
    );
    onChange(newParams);
  };

  // Mover para cima
  const moveUp = (index) => {
    if (index === 0) return;
    const newParams = [...parameters];
    [newParams[index - 1], newParams[index]] = [
      newParams[index],
      newParams[index - 1],
    ];
    const reordered = newParams.map((p, i) => ({ ...p, order: i + 1 }));
    onChange(reordered);
  };

  // Mover para baixo
  const moveDown = (index) => {
    if (index === parameters.length - 1) return;
    const newParams = [...parameters];
    [newParams[index], newParams[index + 1]] = [
      newParams[index + 1],
      newParams[index],
    ];
    const reordered = newParams.map((p, i) => ({ ...p, order: i + 1 }));
    onChange(reordered);
  };

  // Toggle expandido/recolhido (usado tanto por campos como por secções)
  const toggleExpanded = (paramId) => {
    setExpandedParams({
      ...expandedParams,
      [paramId]: !expandedParams[paramId],
    });
  };

  // Adicionar opção a select
  const addOption = (paramId) => {
    const param = parameters.find((p) => p.id === paramId);
    if (param) {
      updateParameter(paramId, "options", [...(param.options || []), ""]);
    }
  };

  // Remover opção de select
  const removeOption = (paramId, optionIndex) => {
    const param = parameters.find((p) => p.id === paramId);
    if (param && param.options) {
      const newOptions = param.options.filter((_, i) => i !== optionIndex);
      updateParameter(paramId, "options", newOptions);
    }
  };

  // Atualizar opção de select
  const updateOption = (paramId, optionIndex, value) => {
    const param = parameters.find((p) => p.id === paramId);
    if (param && param.options) {
      const newOptions = [...param.options];
      newOptions[optionIndex] = value;
      updateParameter(paramId, "options", newOptions);
    }
  };

  // Alternar um campo-irmão como operando de um campo calculado
  const toggleFormulaField = (paramId, siblingId) => {
    const param = parameters.find((p) => p.id === paramId);
    if (!param) return;
    const currentFields = param.formula?.fields || [];
    const newFields = currentFields.includes(siblingId)
      ? currentFields.filter((id) => id !== siblingId)
      : [...currentFields, siblingId];
    updateParameter(paramId, "formula", { op: "sum", fields: newFields });
  };

  const needsOptions = (type) => type === "single_select" || type === "multi_select";
  const needsMinMax = (type) => type === "integer" || type === "float";

  return (
    <div className="space-y-4">
      {parameters.length === 0 ? (
        <div className="text-center py-8 text-neutral-500 border-2 border-dashed border-neutral-300 rounded-lg">
          <p className="mb-4">Nenhum campo ou secção definido</p>
          <div className="flex items-center justify-center space-x-3">
            <button
              type="button"
              onClick={addParameter}
              disabled={disabled}
              className="px-4 py-2 bg-neutral-900 text-white rounded-lg hover:bg-neutral-800 disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Adicionar Campo
            </button>
            <button
              type="button"
              onClick={addGroup}
              disabled={disabled}
              className="px-4 py-2 border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50 disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faLayerGroup} className="mr-2" />
              Adicionar Secção
            </button>
          </div>
        </div>
      ) : (
        <>
          {parameters.map((param, index) => {
            const isGroup = param.type === "group";
            const isExpanded = !!expandedParams[param.id];
            const numericSiblings = parameters.filter(
              (p) => p.id !== param.id && (p.type === "integer" || p.type === "float")
            );

            return (
              <div
                key={param.id}
                className={`border rounded-lg shadow-sm ${
                  isGroup
                    ? "border-neutral-300 bg-neutral-50"
                    : "border-neutral-200 bg-white"
                }`}
              >
                {/* Header */}
                <div className="flex items-center justify-between p-3 border-b border-neutral-100">
                  <div className="flex items-center space-x-3">
                    <div className="flex flex-col space-y-1">
                      <button
                        type="button"
                        onClick={() => moveUp(index)}
                        disabled={disabled || index === 0}
                        className="text-neutral-400 hover:text-neutral-600 disabled:opacity-30"
                        title="Mover para cima"
                      >
                        <FontAwesomeIcon icon={faChevronUp} size="xs" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveDown(index)}
                        disabled={disabled || index === parameters.length - 1}
                        className="text-neutral-400 hover:text-neutral-600 disabled:opacity-30"
                        title="Mover para baixo"
                      >
                        <FontAwesomeIcon icon={faChevronDown} size="xs" />
                      </button>
                    </div>
                    <FontAwesomeIcon
                      icon={isGroup ? faLayerGroup : faGripVertical}
                      className="text-neutral-400"
                    />
                    <span className="text-sm font-medium text-neutral-600">
                      #{param.order}
                    </span>
                    <span className="font-medium text-neutral-800">
                      {param.name || (isGroup ? "(Secção sem nome)" : "(Sem nome)")}
                    </span>
                    {isGroup ? (
                      <span className="text-xs text-neutral-500 bg-neutral-200 px-2 py-0.5 rounded">
                        Secção · {(param.children || []).length} item(ns)
                      </span>
                    ) : (
                      <>
                        {param.required && (
                          <span className="text-red-500 text-xs">*Obrigatório</span>
                        )}
                        <span className="text-xs text-neutral-500 bg-neutral-100 px-2 py-0.5 rounded">
                          {PARAMETER_TYPES.find((t) => t.value === param.type)?.label ||
                            param.type}
                        </span>
                      </>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => toggleExpanded(param.id)}
                      className="text-neutral-500 hover:text-neutral-700 p-1"
                      title={isExpanded ? "Recolher" : "Expandir"}
                    >
                      <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeParameter(param.id)}
                      disabled={disabled}
                      className="text-red-500 hover:text-red-700 p-1 disabled:opacity-50"
                      title={isGroup ? "Remover secção" : "Remover campo"}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>

                {/* Conteúdo expandido */}
                {isExpanded && isGroup && (
                  <div className="p-4 space-y-4">
                    <div>
                      <label className="block text-sm text-neutral-700 mb-1">
                        Nome da Secção *
                      </label>
                      <input
                        type="text"
                        value={param.name || ""}
                        onChange={(e) => updateParameter(param.id, "name", e.target.value)}
                        disabled={disabled}
                        placeholder="Ex: Pasteurização, Fermentação, Salmoura"
                        className="w-full md:w-1/2 px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-400 disabled:bg-neutral-100"
                      />
                    </div>

                    <div className="pl-2 border-l-2 border-neutral-300">
                      <ParameterTemplateEditor
                        parameters={param.children || []}
                        onChange={(children) => updateParameter(param.id, "children", children)}
                        disabled={disabled}
                      />
                    </div>
                  </div>
                )}

                {isExpanded && !isGroup && (
                  <div className="p-4 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Nome do campo */}
                      <div>
                        <label className="block text-sm text-neutral-700 mb-1">
                          Nome do Campo *
                        </label>
                        <input
                          type="text"
                          value={param.name || ""}
                          onChange={(e) =>
                            updateParameter(param.id, "name", e.target.value)
                          }
                          disabled={disabled}
                          placeholder="Ex: Temperatura, Data de Produção"
                          className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-400 disabled:bg-neutral-100"
                        />
                      </div>

                      {/* Tipo do campo */}
                      <div>
                        <label className="block text-sm text-neutral-700 mb-1">
                          Tipo de Campo *
                        </label>
                        <select
                          value={param.type || "text"}
                          onChange={(e) =>
                            updateParameter(param.id, "type", e.target.value)
                          }
                          disabled={disabled}
                          className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-400 disabled:bg-neutral-100"
                        >
                          {PARAMETER_TYPES.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.icon} {type.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {param.type !== "calculated" && (
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id={`required-${param.id}`}
                          checked={param.required || false}
                          onChange={(e) =>
                            updateParameter(param.id, "required", e.target.checked)
                          }
                          disabled={disabled}
                          className="h-4 w-4 text-neutral-900 rounded border-neutral-300"
                        />
                        <label
                          htmlFor={`required-${param.id}`}
                          className="ml-2 text-sm text-neutral-700"
                        >
                          Campo obrigatório
                        </label>
                      </div>
                    )}

                    {needsMinMax(param.type) && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm text-neutral-700 mb-1">
                            Valor Mínimo
                          </label>
                          <input
                            type="number"
                            value={param.min ?? ""}
                            onChange={(e) =>
                              updateParameter(
                                param.id,
                                "min",
                                e.target.value === "" ? undefined : Number(e.target.value)
                              )
                            }
                            disabled={disabled}
                            placeholder="Sem limite"
                            step={param.type === "float" ? "0.01" : "1"}
                            className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-400 disabled:bg-neutral-100"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-neutral-700 mb-1">
                            Valor Máximo
                          </label>
                          <input
                            type="number"
                            value={param.max ?? ""}
                            onChange={(e) =>
                              updateParameter(
                                param.id,
                                "max",
                                e.target.value === "" ? undefined : Number(e.target.value)
                              )
                            }
                            disabled={disabled}
                            placeholder="Sem limite"
                            step={param.type === "float" ? "0.01" : "1"}
                            className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-400 disabled:bg-neutral-100"
                          />
                        </div>
                      </div>
                    )}

                    {needsOptions(param.type) && (
                      <div>
                        <label className="block text-sm text-neutral-700 mb-2">
                          Opções de Seleção
                        </label>
                        <div className="space-y-2">
                          {(param.options || []).map((option, optionIndex) => (
                            <div key={optionIndex} className="flex items-center space-x-2">
                              <span className="text-neutral-400 text-sm w-6">
                                {optionIndex + 1}.
                              </span>
                              <input
                                type="text"
                                value={option}
                                onChange={(e) =>
                                  updateOption(param.id, optionIndex, e.target.value)
                                }
                                disabled={disabled}
                                placeholder={`Opção ${optionIndex + 1}`}
                                className="flex-1 px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-400 disabled:bg-neutral-100"
                              />
                              <button
                                type="button"
                                onClick={() => removeOption(param.id, optionIndex)}
                                disabled={disabled}
                                className="text-red-500 hover:text-red-700 p-2 disabled:opacity-50"
                                title="Remover opção"
                              >
                                <FontAwesomeIcon icon={faTrash} size="sm" />
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => addOption(param.id)}
                            disabled={disabled}
                            className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
                          >
                            <FontAwesomeIcon icon={faPlus} className="mr-1" />
                            Adicionar Opção
                          </button>
                        </div>
                      </div>
                    )}

                    {param.type === "calculated" && (
                      <div>
                        <label className="block text-sm text-neutral-700 mb-2">
                          Campos somados (valor calculado automaticamente)
                        </label>
                        {numericSiblings.length === 0 ? (
                          <p className="text-sm text-neutral-500 italic">
                            Adicione pelo menos dois campos numéricos neste mesmo nível
                            para escolher os operandos.
                          </p>
                        ) : (
                          <div className="space-y-1">
                            {numericSiblings.map((sibling) => (
                              <label
                                key={sibling.id}
                                className="flex items-center space-x-2 cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={(param.formula?.fields || []).includes(sibling.id)}
                                  onChange={() => toggleFormulaField(param.id, sibling.id)}
                                  disabled={disabled}
                                  className="h-4 w-4 text-neutral-900 rounded border-neutral-300"
                                />
                                <span className="text-sm text-neutral-700">
                                  {sibling.name || "(Sem nome)"}
                                </span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Botões adicionar mais */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={addParameter}
              disabled={disabled}
              className="flex-1 py-3 border-2 border-dashed border-neutral-300 rounded-lg text-neutral-600 hover:border-neutral-400 hover:text-neutral-700 transition-colors disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Adicionar Campo
            </button>
            <button
              type="button"
              onClick={addGroup}
              disabled={disabled}
              className="flex-1 py-3 border-2 border-dashed border-neutral-300 rounded-lg text-neutral-600 hover:border-neutral-400 hover:text-neutral-700 transition-colors disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faLayerGroup} className="mr-2" />
              Adicionar Secção
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ParameterTemplateEditor;
